import { InboxIcon } from "@hugeicons-pro/core-stroke-rounded";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { Fragment, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Button, ButtonSize, ButtonVariant, Show } from "shared/components";
import { IconAILeasingAgent } from "shared/components/ai/IconAI/icons/IconAILeasingAgent";
import {
  FollowUpFlagIcon,
  FollowUpSecondaryTags,
  followedUpSummary,
  followUpSentence,
  followUpShortLabel,
  isFollowUpOpen
} from "shared/app/CallFollowUp";
import { cx, formatPhone } from "shared/utils";
import { formatCalendar } from "../helpers/format-calendar";
import { Notification, NotificationType } from "../types";

type FollowUpMetadata = Notification["metadata"];

const ACTIVATION_KEYS = ["Enter", " "];

// Reply lands on the caller's thread; the lead is the fallback for a call the
// inbox has not threaded yet.
const inboxDestination = (metadata: FollowUpMetadata): string | null => {
  if (metadata.contact_id)
    return `/inbox?contact=${encodeURIComponent(metadata.contact_id)}`;
  if (metadata.lead_id) return `/leads/${encodeURIComponent(metadata.lead_id)}`;
  return null;
};

const callLogDestination = (metadata: FollowUpMetadata): string =>
  metadata.call_transcript_id
    ? `/calls?tab=log&call=ai_transcript:${encodeURIComponent(metadata.call_transcript_id)}`
    : "/calls?tab=log";

const callerLabel = (metadata: FollowUpMetadata): string => {
  const name = metadata.caller_name?.trim() || "Unknown caller";
  const phone =
    metadata.caller_phone_number ||
    metadata.from_number ||
    metadata.caller_number;
  return phone ? `${name} · ${formatPhone(phone)}` : name;
};

const escalationLabel = ({ type, metadata }: Notification): string => {
  if (metadata.follow_up) return followUpShortLabel(metadata.follow_up);
  if (metadata.escalation_label) return metadata.escalation_label;
  return metadata.was_transferred || type === NotificationType.AiCallEscalated
    ? "Caller requests escalation"
    : "";
};

const emailedTo = (metadata: FollowUpMetadata): string | null => {
  const recipients = metadata.email_recipients ?? [];
  if (metadata.escalation_channel !== "email" || recipients.length === 0)
    return null;
  return recipients.length === 1
    ? recipients[0]
    : `${recipients.length} people`;
};

const BoxChip = ({
  children,
  className,
  withDot = true
}: {
  children: ReactNode;
  className?: string;
  withDot?: boolean;
}) => (
  <span
    className={cx(
      "inline-flex h-6 shrink-0 items-center gap-1.5 rounded-full px-2 text-[12px] font-medium leading-4",
      className
    )}
  >
    {withDot ? (
      <span className="size-[5px] shrink-0 rounded-full bg-current opacity-75" />
    ) : null}
    {children}
  </span>
);

const Box = ({
  children,
  className
}: {
  children: ReactNode;
  className?: string;
}) => (
  <div
    className={cx(
      "flex min-h-[46px] min-w-0 items-center gap-3 rounded-lg px-3.5 text-sm",
      className
    )}
  >
    {children}
  </div>
);

const SeparatedText = ({ parts }: { parts: string[] }) => (
  <span className="flex min-w-0 items-center truncate">
    {parts.map((part, index) => (
      <Fragment key={`${index}-${part}`}>
        {index > 0 ? (
          <span className="mx-2 size-1 shrink-0 rounded-full bg-current opacity-50" />
        ) : null}
        <span className="truncate">{part}</span>
      </Fragment>
    ))}
  </span>
);

interface AICallFollowUpNotificationCardProps {
  item: Notification;
}

export const AICallFollowUpNotificationCard = ({
  item
}: AICallFollowUpNotificationCardProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id, read_at, metadata, created_at } = item;
  const followUp = metadata.follow_up ?? null;
  const isOpen = isFollowUpOpen(followUp);
  const replyDestination = inboxDestination(metadata);

  const { mutate: markAsRead } = useMutation({
    mutationKey: ["NOTIFICATIONS", "MARK_AS_READ"],
    mutationFn: (ids: number[]) =>
      instance.post("/portal/v1/notifications/mark_as_read", { ids }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["NOTIFICATIONS"] });
      queryClient.invalidateQueries({
        queryKey: ["UNREAD_NOTIFICATIONS_COUNT"]
      });
      queryClient.invalidateQueries({
        queryKey: ["TOTAL_UNREAD_NOTIFICATIONS"]
      });
      queryClient.invalidateQueries({ queryKey: ["NEWEST_NOTIFICATIONS"] });
    }
  });

  const openCall = () => {
    if (!read_at) markAsRead([id]);
    navigate(callLogDestination(metadata));
  };

  const escalation = escalationLabel(item);
  const escalationEmailTo = emailedTo(metadata);
  const metaParts = [
    escalation && `Escalation: ${escalation}`,
    metadata.agent_name && `Agent: ${metadata.agent_name}`,
    formatCalendar(metadata.occurred_at || created_at)
  ].filter((part): part is string => Boolean(part));

  const propertyName = followUp?.property_name || metadata.property_name;
  const requestParts = followUp
    ? [
        followUpSentence(followUp),
        ...(propertyName ? [propertyName] : []),
        ...(followUp.is_after_hours ? ["After hours"] : []),
        "Notified you"
      ]
    : [];

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={openCall}
      onKeyDown={(event) => {
        if (ACTIVATION_KEYS.includes(event.key)) {
          event.preventDefault();
          openCall();
        }
      }}
      className="flex cursor-pointer items-start gap-6 bg-white p-5 hover:bg-alpha-20"
    >
      <span
        className={cx(
          "mt-1 size-2.5 shrink-0 rounded-full",
          read_at ? "bg-alpha-80" : "bg-primary-500"
        )}
      />
      <IconAILeasingAgent bgSize={40} />

      <div className="flex min-w-0 flex-1 flex-col gap-4">
        <div className="flex min-h-5 flex-wrap items-center gap-4">
          <p className="whitespace-nowrap text-sm font-bold text-content-1000">
            Intervention need detected
          </p>
          <span className="ml-auto text-[13px] leading-5 text-content-300">
            <SeparatedText parts={metaParts} />
          </span>
        </div>

        <div className="flex flex-col gap-2">
          <Box className="bg-background-900 text-content-700">
            <BoxChip className="bg-alpha-80 text-content-400">
              Caller: {callerLabel(metadata)}
            </BoxChip>
            <Show if={Boolean(metadata.caller_quote)}>
              <span className="min-w-0 truncate">{metadata.caller_quote}</span>
            </Show>
          </Box>

          <Show if={Boolean(followUp) && isOpen}>
            <Box className="bg-danger-50 pr-2 text-danger-700">
              <BoxChip
                withDot={false}
                className="gap-1 bg-danger-500/[.14] font-bold text-danger-700"
              >
                <FollowUpFlagIcon />
                Needs follow-up
              </BoxChip>
              <SeparatedText parts={requestParts} />
              <FollowUpSecondaryTags
                followUp={followUp ?? { request_types: [] }}
              />
              <Show if={Boolean(replyDestination)}>
                <Button
                  variant={ButtonVariant.BASE}
                  size={ButtonSize.SM}
                  HugeIcon={InboxIcon}
                  className="ml-auto shrink-0"
                  onClick={(event) => {
                    event.stopPropagation();
                    if (!read_at) markAsRead([id]);
                    if (replyDestination) navigate(replyDestination);
                  }}
                >
                  Reply
                </Button>
              </Show>
            </Box>
          </Show>

          <Show if={Boolean(followUp) && !isOpen}>
            <Box className="bg-success-50 text-success-700">
              <BoxChip
                withDot={false}
                className="bg-success-600/[.16] font-bold text-success-700"
              >
                Followed up
              </BoxChip>
              <span className="min-w-0 truncate">
                {followUp ? followedUpSummary(followUp) : ""}
              </span>
            </Box>
          </Show>

          <Show if={!followUp && Boolean(escalationEmailTo)}>
            <Box className="bg-primary-20 text-primary-600">
              <BoxChip className="bg-primary-50 text-primary-600">
                Escalation email to
              </BoxChip>
              <span className="min-w-0 truncate">{escalationEmailTo}</span>
            </Box>
          </Show>

          <Show if={!followUp && Boolean(metadata.was_transferred)}>
            <Box className="bg-primary-20 text-primary-600">
              <BoxChip className="bg-primary-50 text-primary-600">
                Transferred to
              </BoxChip>
              <span className="min-w-0 truncate">
                {metadata.transferred_to_number
                  ? formatPhone(metadata.transferred_to_number)
                  : ""}
              </span>
            </Box>
          </Show>
        </div>
      </div>
    </div>
  );
};
